// src/encounter/note-column/to-note-order-detail.ts
//
// The A/P order row's SECOND line — Flutter's `e.description` / `otherInfo`
// (`assesment_plan_note_component_widget.dart:509-515`, drawn under the item title in
// `labelTextStyleTextColor`).
//
// WHY A SUPPLIER FUNCTION RATHER THAN A FIELD
// -------------------------------------------
// The DS `PlanOrder` is `{ id, type, label, dxIds }` (`ap-shared.d.ts:18-24`) — it cannot carry a
// code or a sig. The richer detail lives in the host's `OrderPayloadMap`
// (`lib/order-payload.ts:111`), keyed by order id. `toApNoteSections` therefore takes an
// `orderDetail` callback, and this is the one the encounter hosts pass.
//
// ANTI-FICTION: every branch returns `undefined` rather than a placeholder when the payload has
// nothing to say. A row with no second line is correct; a row with an invented code is not.
import type { PlanOrder } from '@scripsteam/scrips-signal-ds';
import type { CodedOrderPayload, OrderPayloadMap } from '../lib/order-payload';

/** Trim to `undefined` — an empty second line must not reserve a row. */
function clean(value: string | null | undefined): string | undefined {
  const t = (value ?? '').trim();
  return t.length > 0 ? t : undefined;
}

/**
 * A medication's detail is its SIG when the clinician wrote one — that is the instruction the note
 * exists to carry. Failing that, the structured dose the dialog captured.
 *
 * FIELD NAMES ARE THE PAYLOAD'S, NOT GUESSED: there is no `dose` on this type. Units-per-intake is
 * `quantity` and its unit is `doseUnit` (`lib/order-payload.ts:48,53`); `frequency` is the coded
 * type (`every_day` / `as_needed` / …), rendered with its underscores opened out.
 */
function medicationDetail(p: Extract<CodedOrderPayload, { kind: 'medication' }>): string | undefined {
  const sig = clean(p.sig);
  if (sig) return sig;
  const parts: string[] = [];
  if (p.quantity != null) parts.push(`${p.quantity}${p.doseUnit ? ` ${p.doseUnit}` : ''}`);
  const freq = clean(p.frequency);
  if (freq) parts.push(freq.replace(/_/g, ' '));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/** The coded orders (lab / imaging / procedure / vaccination) show their own code. */
function codedDetail(p: CodedOrderPayload): string | undefined {
  const coded = p as { code?: { code?: string | null; displayName?: string | null } };
  return clean(coded.code?.code);
}

/**
 * Build the `orderDetail` callback for `toApNoteSections`. Pass the host's live `payloads` map;
 * an order with no payload (or a payload with no detail) renders name-only.
 */
export function noteOrderDetail(payloads: OrderPayloadMap): (order: PlanOrder) => string | undefined {
  return (order: PlanOrder) => {
    const payload = payloads.get(order.id);
    if (!payload) return undefined;
    if (payload.kind === 'medication') return medicationDetail(payload);
    return codedDetail(payload);
  };
}
